/**
 * Staff app hosts: `<slug>.hausverwalter.ai` per org, apex = claim-less entry.
 * Pure strings, shared by Convex, Next middleware/routes and client bundles.
 */

import {
  RESERVED_PORTAL_SUBDOMAINS,
  normalizeHostname,
} from "../portal/portal_host";

/** Production staff apex — always recognized even if build env points elsewhere. */
export const PRODUCTION_STAFF_APEX = "hausverwalter.ai";

function readStaffApexDomain(): string {
  const fromEnv =
    typeof process !== "undefined" && process.env
      ? process.env.NEXT_PUBLIC_STAFF_APEX_DOMAIN ??
        process.env.STAFF_APEX_DOMAIN ??
        ""
      : "";
  const trimmed = fromEnv.trim().toLowerCase().replace(/^\.+/, "");
  return trimmed || PRODUCTION_STAFF_APEX;
}

export const STAFF_APEX_DOMAIN = readStaffApexDomain();

/** Labels with own DNS records (mail, LiveKit, Nylas, mail sending) or infra use. Never an org host. */
export const RESERVED_STAFF_SUBDOMAINS: ReadonlySet<string> = new Set([
  ...RESERVED_PORTAL_SUBDOMAINS,
  "autodiscover",
  "send",
  "live",
  "live-turn",
  "scan",
  "notifications",
  "video",
  // Provider help center (`docs_host.ts`).
  "docs",
]);

const SLUG_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function resolveStaffApexForHost(hostname: string): string | null {
  for (const apex of [PRODUCTION_STAFF_APEX, STAFF_APEX_DOMAIN]) {
    if (hostname === apex || hostname.endsWith(`.${apex}`)) return apex;
  }
  return null;
}

/** Staff apex or any of its subdomains. */
export function isStaffHostname(host: string | null | undefined): boolean {
  const hostname = normalizeHostname(host);
  return hostname !== "" && resolveStaffApexForHost(hostname) !== null;
}

/** True when a slug can serve as its own staff host label. */
export function isUsableStaffSlug(slug: string): boolean {
  return SLUG_LABEL_RE.test(slug) && !RESERVED_STAFF_SUBDOMAINS.has(slug);
}

/** Org label of `<slug>.hausverwalter.ai`; null for apex, reserved, nested or foreign hosts. */
export function extractStaffSlugFromHost(
  host: string | null | undefined,
): string | null {
  const hostname = normalizeHostname(host);
  if (!hostname) return null;
  const apex = resolveStaffApexForHost(hostname);
  if (!apex || hostname === apex) return null;
  const label = hostname.slice(0, hostname.length - apex.length - 1);
  if (!label || label.includes(".")) return null;
  return isUsableStaffSlug(label) ? label : null;
}

/** Org host as normalized hostname, or null. Claims and cookies compare this exact string. */
export function staffOrgHostname(
  host: string | null | undefined,
): string | null {
  return extractStaffSlugFromHost(host) === null
    ? null
    : normalizeHostname(host);
}

/** Apex, `www.` or `app.` — staff hosts without an org of their own. */
export function isStaffEntryHost(host: string | null | undefined): boolean {
  const hostname = normalizeHostname(host);
  const apex = resolveStaffApexForHost(hostname);
  if (!apex) return false;
  return (
    hostname === apex ||
    hostname === `www.${apex}` ||
    hostname === `app.${apex}`
  );
}

/** `.hausverwalter.ai` on every staff host so one login covers all org hosts. */
export function staffCookieDomainForHost(
  host: string | null | undefined,
): string | null {
  const hostname = normalizeHostname(host);
  if (!hostname) return null;
  const apex = resolveStaffApexForHost(hostname);
  return apex ? `.${apex}` : null;
}

export function buildStaffOrgOrigin(slug: string): string {
  return `https://${slug}.${STAFF_APEX_DOMAIN}`;
}

export function buildStaffEntryOrigin(): string {
  return `https://${STAFF_APEX_DOMAIN}`;
}

/** Public org pages (booking, forms, help, shares) live on the org host; localhost and previews keep `appOrigin`. */
export function orgPublicOriginFor(
  appOrigin: string,
  orgSlug: string | null | undefined,
): string {
  const origin = appOrigin.trim().replace(/\/+$/, "");
  if (!orgSlug || !isUsableStaffSlug(orgSlug)) return origin;
  let hostname: string;
  try {
    hostname = new URL(origin).hostname;
  } catch {
    return origin;
  }
  return isStaffHostname(hostname) ? buildStaffOrgOrigin(orgSlug) : origin;
}
